import React, { useEffect, useState } from 'react';
import { Button, Card, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api_url } from '../../constant/constant';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const CampaignUsersSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedDepartment, setSelectedDepartment] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState([]);
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [quizDaily, setQuizDaily] = useState([]);
  const [quizPerLesson, setQuizPerLesson] = useState([]);
  const [videoWatchedDaily, setVideoWatchedDaily] = useState([]);
  const [videoWatchedPerLesson, setVideoWatchedPerLesson] = useState([]);
  const [quizPercentages, setQuizPercentages] = useState({ passed: 0, notTaken: 0 });
  const [activitySummary, setActivitySummary] = useState({ active_users: 0, inactive_users: { data: [] } });
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const colors = ['#34d399', '#60a5fa', '#f87171', '#fbbf24', '#a78bfa', '#4ade80'];
  const pieColors = ['#10b981', '#ef4444', '#d1d5db'];

  useEffect(() => {
    fetchDepartments();
    fetchCampaigns();
  }, []);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/admin/main/departments`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result && result.data) setDepartments(result.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/learning-campaigns`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (result.learning_campaigns?.data) setCampaigns(result.learning_campaigns.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchData = async () => {
    if (!selectedCampaign || !selectedDepartment) return;

    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(
        `${api_url}/report/learning/campaign-users-summary?campaign_id=${selectedCampaign}&department_id=${selectedDepartment}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if ([401, 403, 429].includes(response.status)) {
        localStorage.removeItem('userToken');
        navigate('/login');
        return;
      }

      const result = await response.json();

      if (result.status === 'success') {
        setCampaignInfo(result.campaign);
        setQuizDaily(result.quiz_stats?.quiz_taken_daily || []);
        setQuizPerLesson(result.quiz_stats?.quiz_per_lesson || []);
        setVideoWatchedDaily(result.video_stats?.watched_daily || []);
        setVideoWatchedPerLesson(result.video_stats?.watched_per_lesson || []);
        setQuizPercentages({
          passed: result.quiz_stats?.passed_percentage || 0,
          notTaken: result.quiz_stats?.not_taken_percentage || 0,
        });
        setActivitySummary((prev) => ({
          ...prev,
          active_users: result.activity_summary?.active_users || 0,
          inactive_users: result.inactive_users || { data: [] },
        }));
        setActiveUsers(result.active_users?.data || []);
        setInactiveUsers(result.inactive_users?.data || []);
      } else {
        throw new Error(result.message || 'Failed to fetch campaign data.');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'An error occurred while fetching data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInactivePageClick = async (url) => {
    if (!url) return;
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      const result = await response.json();
      if (result.status === 'success') {
        setInactiveUsers(result.inactive_users?.data || []);
        setActivitySummary((prev) => ({
          ...prev,
          inactive_users: result.inactive_users,
        }));
      } else {
        throw new Error(result.message || 'Failed to paginate inactive users.');
      }
    } catch (err) {
      console.error('Pagination error:', err);
      setError(err.message || 'Error while paginating.');
    } finally {
      setIsLoading(false);
    }
  };
  

  const pieData = [
    { name: t('Passed'), value: quizPercentages.passed },
    { name: t('Not Taken'), value: quizPercentages.notTaken },
  ];

  return (

    
    <div className='container py-4 mb-4'>
      <div className='container p-3'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <div className='pageTitle'>{t('TrainingCampaignDashboard')}</div>
          <Button variant='primary' onClick={() => navigate(-1)}>
            {t('Back')}
          </Button>
        </div>
      </div>

      <div className='mb-4 p-3 card'>
        <div className='p-3' style={{ backgroundColor: '#f0f4f8', borderRadius: '16px' }}>
          <div className='row g-3 align-items-center'>
            <div className='col-md-4'>
              <label className='form-label'>{t('SelectCampaign')}</label>
              <select
                className='form-select'
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
              >
                <option value=''>{t('SelectCampaign')}</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.title_en}
                  </option>
                ))}
              </select>
            </div>

            <div className='col-md-4'>
              <label className='form-label'>{t('SelectDepartments')}</label>
              <select
                className='form-select'
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value=''>{t('SelectDepartment')}</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Button
                style={{ minWidth: '100px' }}
                className='btn btn-success '
                onClick={fetchData}
                disabled={!selectedCampaign}
              >
                {t('ApplyFilters')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isLoading && <div className='text-center py-5'>{t('Loading')}...</div>}
      {error && <div className='text-danger text-center py-5'>{error}</div>}

      <div className='row g-3 mb-4'>
        <div className='col-md-3'>
          <Card className='p-3 text-center'>
            <h4 className='mb-1 fw-bold'>{campaignInfo?.start_date || '-'}</h4>
            <small className='text-muted'>{t('CampaignStartDate')}</small>
          </Card>
        </div>
        <div className='col-md-3'>
          <Card className='p-3 text-center'>
            <h4 className='mb-1 fw-bold'>{campaignInfo?.end_date || '-'}</h4>
            <small className='text-muted'>{t('CampaignEndDate')}</small>
          </Card>
        </div>
        <div className='col-md-3'>
          <Card className='p-3 text-center'>
            <h4 className='mb-1 fw-bold'>{campaignInfo?.total_users || 0}</h4>
            <small className='text-muted'>{t('NumberOfEnrolledUsers')}</small>
          </Card>
        </div>
        <div className='col-md-3'>
          <Card className='p-3 text-center'>
            <h4 className='mb-1 fw-bold'>{campaignInfo?.total_lessons || 0}</h4>
            <small className='text-muted'>{t('NumberOfLessons')}</small>
          </Card>
        </div>
      </div>

      <div className='row g-3 mb-4'>
        <div className='col-md-6'>
          <Card className='p-3 text-center'>
            <h4 className='mb-1 fw-bold'>{activitySummary.active_users}</h4>
            <small className='text-success'>{t('ActiveUsers')}</small>
          </Card>
        </div>
        <div className='col-md-6'>
          <Card className='p-3 text-center'>
            <h4 className='mb-1 fw-bold'>{activitySummary.inactive_users?.total || 0}</h4>
            <small className='text-danger'>{t('InactiveUsers')}</small>
          </Card>
        </div>
      </div>

      {/* Active Users Table */}
      <Card className='shadow-sm p-4 mb-5'>
        <h5 className='mb-4'>{t('ActiveUsers')}</h5>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>{t('Username')}</th>
              <th>{t('Email')}</th>
              <th>{t('FirstName')}</th>
              <th>{t('LastName')}</th>
              <th>{t('Department')}</th>
              <th>{t('LastLogin')}</th>
              <th>{t('VideosWatched')}</th>
              <th>{t('QuizzesSolved')}</th>
              <th>{t('QuizzesPassed')}</th>
              <th>{t('QuizzesFailed')}</th>
            </tr>
          </thead>
          <tbody>
            {activeUsers.length === 0 ? (
              <tr>
                <td colSpan={10} className='text-center'>
                  {/* {t('NoActiveUsers')} */}
                </td>
              </tr>
            ) : (
              activeUsers.map((user) => (
                <tr key={user.id || user.username}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.first_name}</td>
                  <td>{user.last_name}</td>
                  <td>{user.department}</td>
                  <td>{user.last_login || '-'}</td>
                  <td>{user.watched_videos}</td>
                  <td>{user.solved_quizzes}</td>
                  <td>{user.passed_quizzes}</td>
                  <td>{user.failed_quizzes}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {/* Inactive Users Table (Always Show + Pagination) */}
     {/* Inactive Users Table */}
<Card className='shadow-sm p-4 mb-5'>
  <h5 className='mb-4'>{t('InactiveUsers')}</h5>
  <Table striped bordered hover responsive>
    <thead>
      <tr>
        <th>{t('Username')}</th>
        <th>{t('Email')}</th>
        <th>{t('FirstName')}</th>
        <th>{t('LastName')}</th>
        <th>{t('Department')}</th>
        <th>{t('LastLogin')}</th>
        <th>{t('VideosWatched')}</th>
        <th>{t('QuizzesSolved')}</th>
        <th>{t('QuizzesPassed')}</th>
        <th>{t('QuizzesFailed')}</th>
      </tr>
    </thead>
    <tbody>
      {inactiveUsers.length === 0 ? (
        <tr>
          <td colSpan={10} className='text-center text-muted'>
            {/* {t('NoInactiveUsers')} */}
          </td>
        </tr>
      ) : (
        inactiveUsers.map((user, index) => (
          <tr key={index}>
            <td>{user.username}</td>
            <td>{user.email}</td>
            <td>{user.first_name}</td>
            <td>{user.last_name}</td>
            <td>{user.department}</td>
            <td>{user.last_login || '-'}</td>
            <td>{user.watched_videos}</td>
            <td>{user.solved_quizzes}</td>
            <td>{user.passed_quizzes}</td>
            <td>{user.failed_quizzes}</td>
          </tr>
        ))
      )}
    </tbody>
  </Table>

  {/* Pagination */}
  <div className='d-flex justify-content-end mt-3 flex-wrap'>
    {activitySummary?.inactive_users?.links?.map((link, idx) => (
      <Button
        key={idx}
        variant={link.active ? 'secondary' : 'outline-secondary'}
        className='mx-1 mb-2 btn-sm'
        style={{ minWidth: '40px' }}
        disabled={!link.url}
        dangerouslySetInnerHTML={{ __html: link.label }}
        onClick={() => handleInactivePageClick(link.url)}
      />
    ))}
  </div>
</Card>

    </div>
  );
};

export default CampaignUsersSummary;
